import {useSelector} from "react-redux";
import {css} from "@panda/css";

import {Container} from "@pg-design/grid-module";
import {Text} from "@pg-design/text-module";
import {SkeletonBox} from "@pg-mono/skeleton-box";
import {priceFormat, priceM2Format, roomsFormat} from "@pg-mono/string-utils";

import {IRPStore} from "../../../app/rp_reducer";
import {IRegionReportPrice} from "../../../region/types/IRegionReportPrice";
import {getTransactionPricesPeriod} from "../actions/fetch_transaction_prices";
import {useGetRcnDataQuery} from "../api/get_rcn_data_api";
import {PriceComparisonCard} from "../atoms/PriceComparisonCard";
import {isAllRooms} from "../constants/rcn_rooms_filter";
import {TRANSACTION_PRICES_GRAIN, TRANSACTION_PRICES_METRIC} from "../constants/transaction_prices";
import {PricesCityAnalyticsLocation} from "../tracking/prices_city_analytics";
import {formatMonthLabel} from "../utils/format_month_label";
import {formatRcnPeriodLabel} from "../utils/format_rcn_period_label";
import {formatTransactionsCountGenitive} from "../utils/format_transactions_count";
import {getAvgPriceM2ForRooms} from "../utils/get_avg_price_m2_for_rooms";
import {getFlatTypeGenitiveLabel} from "../utils/get_flat_type_genitive_label";
import {getLatestPriceReport} from "../utils/get_latest_price_report";
import {getMedianPriceM2} from "../utils/get_median_price_m2";
import {getPeriodOfferPriceM2} from "../utils/get_period_offer_price_m2";
import {getPricesAvailableDate} from "../utils/get_prices_available_date";
import {getRcnPriceStatsQuery} from "../utils/get_rcn_params";
import {getSignedPercentComma} from "../utils/percents_handling_helpers";
import {PricesCityViewSectionTracker} from "./PricesCityViewSectionTracker";
import {usePricesContext} from "./PricesContextFilterProvider";
import {PricesRegionSchemaDataset} from "./PricesRegionSchemaDataset";
import {PriceStatSwitch} from "./PriceStatSwitch";
import {RoomsFilterSelect} from "./RoomsFilterSelect";

interface Props {
    region: IRegionReportPrice;
    location: PricesCityAnalyticsLocation;
}

// Below this, the negotiation summary reads as "prices were close" rather than a forced %.
const NEGOTIATION_STABILITY_THRESHOLD_PERCENT = 1;

export const PricesDifferences = (props: Props) => {
    const {region, location} = props;
    const {rooms, priceStat} = usePricesContext();

    const prices = useSelector((store: IRPStore) => store.report.price.prices);
    const pricesAvailableDate = useSelector((store: IRPStore) => store.report.price.pricesAvailableDate);
    const referenceDate = getPricesAvailableDate(pricesAvailableDate?.date);

    // The hub table's query for this one region; only the rooms filter and the median switch tell them apart.
    const {data: rcnData, isFetching} = useGetRcnDataQuery(
        getRcnPriceStatsQuery(region.id, referenceDate, rooms, 1, TRANSACTION_PRICES_GRAIN, priceStat === "median" ? "median" : TRANSACTION_PRICES_METRIC)
    );
    // The quarter the hub table reports, so the city's figures match its row there.
    const reportedPeriod = getTransactionPricesPeriod(referenceDate);

    const currentMonthData = getLatestPriceReport(prices);

    if (!prices || !currentMonthData) {
        return null;
    }

    const currentOfferPriceM2 = priceStat === "median" ? getMedianPriceM2(currentMonthData) : getAvgPriceM2ForRooms(currentMonthData, rooms);

    const latestRcnPoint = rcnData?.series[0];
    const hasRcnData = latestRcnPoint !== undefined;
    // Only the reported quarter is shown - an older point just proves the registry has data for this region.
    const lastRcnPeriod = latestRcnPoint?.period_start === reportedPeriod.period_start ? latestRcnPoint : undefined;
    const transactionalPriceM2 = lastRcnPeriod?.value ?? null;

    // Falls back to the reported period when RCN has no data at all (e.g. Bydgoszcz).
    const offerPeriod = lastRcnPeriod ?? reportedPeriod;

    // Weighted average across the quarter's months (by offer count), not just one noisy month.
    const matchedOfferPriceM2 = getPeriodOfferPriceM2({
        history: prices,
        filter: (monthData) => monthData.slug_city === currentMonthData.slug_city,
        periodStart: offerPeriod.period_start,
        periodLabel: offerPeriod.period_label,
        getPriceM2: (monthData) => (priceStat === "median" ? getMedianPriceM2(monthData) : getAvgPriceM2ForRooms(monthData, rooms)),
        getWeight: (monthData) => monthData.available_offers
    });

    const isPricesLoading = isFetching;

    const differenceM2 = matchedOfferPriceM2 && transactionalPriceM2 ? matchedOfferPriceM2 - transactionalPriceM2 : null;
    const differencePercent = differenceM2 && matchedOfferPriceM2 ? Math.round((differenceM2 / matchedOfferPriceM2) * 1000) / 10 : null;

    return (
        <Container className={containerStyle}>
            <PricesCityViewSectionTracker sectionName="price_differences" location={location} />

            <div className={headerRowStyle}>
                <div>
                    <Text variant="headline_3" as="h2" mb={2}>
                        Ile kosztuje nowe mieszkanie {region.name_declension_where}
                    </Text>

                    <Text variant="body_copy_1" color="var(--colors-gray-700)" mb={2}>
                        Pokazujemy mediany i średnie ceny ofertowe z naszej bazy – czyli kwoty, za jakie deweloperzy oferują mieszkania – i zestawiamy je z
                        rzeczywistymi cenami transakcyjnymi z RCN. Dzięki temu widzisz, ile faktycznie płacą kupujący i jaka jest przestrzeń do negocjacji.
                    </Text>
                </div>
                <div className={priceStatSwitchWrapperStyle}>
                    <PriceStatSwitch location={location} />
                    <RoomsFilterSelect instanceId="prices-differences-rooms-select" sectionName="price_differences" location={location} />
                </div>
            </div>

            <div className={cardsRowStyle}>
                <PriceComparisonCard
                    variant="white"
                    title="Aktualna cena ofertowa"
                    priceM2={currentOfferPriceM2}
                    description="Aktualna kwota oczekiwana przez dewelopera – punkt wyjścia do negocjacji, nie ostateczna kwota transakcji."
                    source={`Źródło: baza ofert rynekpierwotny.pl · ${formatMonthLabel(currentMonthData.date)}`}
                    isLoading={isPricesLoading}
                    className={css({md: {minW: "320px"}})}
                />

                <div className={pairedCardsStyle}>
                    <PriceComparisonCard
                        variant="white"
                        title={`Cena ofertowa (${formatRcnPeriodLabel(offerPeriod.period_label)})`}
                        priceM2={matchedOfferPriceM2}
                        description={`${priceStat === "median" ? "Mediana kwoty" : "Średnia kwota"}, jakiej deweloper oczekiwał w ogłoszeniach w ${formatRcnPeriodLabel(offerPeriod.period_label)}.`}
                        source={`Źródło: baza ofert rynekpierwotny.pl · ${formatRcnPeriodLabel(offerPeriod.period_label)}`}
                        isLoading={isPricesLoading}
                    />

                    <PriceComparisonCard
                        variant="beige"
                        title={`Cena transakcyjna${
                            lastRcnPeriod
                                ? ` (${formatRcnPeriodLabel(lastRcnPeriod.period_label)}${lastRcnPeriod.n_rows < 10 ? ", dane orientacyjne" : ""})`
                                : ""
                        }`}
                        priceM2={transactionalPriceM2}
                        emptyStateMessage={hasRcnData ? "Za mało transakcji" : undefined}
                        description={
                            priceStat === "median"
                                ? "Mediana kwoty faktycznie zapłaconej, wpisanej do aktu notarialnego."
                                : "Średnia kwota faktycznie zapłacona, wpisana do aktu notarialnego."
                        }
                        source={`Źródło: RCN${lastRcnPeriod ? `, ${formatRcnPeriodLabel(lastRcnPeriod.period_label)}` : ""} · rynek pierwotny`}
                        sourceDetails={
                            lastRcnPeriod && (
                                <Text variant="info_txt_1" color="var(--colors-gray-700)" style={{lineHeight: "100%"}}>
                                    <span style={{fontWeight: 600}}>Na podstawie {formatTransactionsCountGenitive(lastRcnPeriod.n_rows)}</span>
                                    {!isAllRooms(rooms) ? ` · ${roomsFormat(rooms)}` : ""} · {region.short_name}
                                </Text>
                            )
                        }
                        isLoading={isPricesLoading}
                    />

                    {isPricesLoading ? (
                        <div className={negotiationBlockStyle}>
                            <div className={negotiationBadgeStyle}>
                                <SkeletonBox isText height="4.8rem" width="80%" />
                            </div>

                            <SkeletonBox isText height="4.8rem" width="100%" />
                        </div>
                    ) : (
                        // Non-null only when lastRcnPeriod, matchedOfferPriceM2 and transactionalPriceM2 all were.
                        differencePercent !== null &&
                        differenceM2 !== null && (
                            <div className={negotiationBlockStyle}>
                                <div className={negotiationBadgeStyle}>
                                    <Text variant="headline_3" as="p" color="var(--colors-secondary)" style={{textAlign: "center"}}>
                                        {getSignedPercentComma(differencePercent, true)}
                                    </Text>
                                    <Text variant="info_txt_1" color="var(--colors-gray-800)" style={{textAlign: "center"}}>
                                        {priceM2Format(Math.abs(differenceM2))}
                                    </Text>
                                </div>

                                <div>
                                    <Text variant="headline_6" as="p" color="var(--colors-gray-800)" mb={1}>
                                        Podsumowanie
                                    </Text>

                                    <Text variant="info_txt_1" color="var(--colors-gray-800)">
                                        W {formatRcnPeriodLabel(lastRcnPeriod!.period_label).replace("kw.", "kwartale")}{" "}
                                        {priceStat === "median" ? "mediana ceny ofertowej" : "średnia cena ofertowa"} {getFlatTypeGenitiveLabel(rooms)}{" "}
                                        {region.name_declension_where} wynosiła {priceM2Format(matchedOfferPriceM2!)}, a cena transakcyjna –{" "}
                                        {priceM2Format(transactionalPriceM2!)}. Oznacza to, że{" "}
                                        {Math.abs(differencePercent) <= NEGOTIATION_STABILITY_THRESHOLD_PERCENT
                                            ? "ceny transakcyjne były zbliżone do ofertowych – kupujący płacili mniej więcej tyle, ile wynikało z ogłoszeń."
                                            : `kupujący płacili${priceStat === "median" ? "" : " średnio"} o ${Math.abs(differencePercent)
                                                  .toString()
                                                  .replace(
                                                      ".",
                                                      ","
                                                  )}% ${differencePercent > 0 ? "mniej" : "więcej"} niż wynikało z ogłoszeń – około ${priceFormat(
                                                  Math.abs(differenceM2)
                                              )} za każdy metr kwadratowy.`}
                                    </Text>
                                </div>
                            </div>
                        )
                    )}
                </div>
            </div>

            <PricesRegionSchemaDataset
                region={region}
                offerPriceM2={matchedOfferPriceM2}
                transactionalPriceM2={transactionalPriceM2}
                lastRcnPeriod={lastRcnPeriod}
            />
        </Container>
    );
};

const containerStyle = css({
    my: 4.5,

    md: {
        my: 8
    },
    lg: {
        px: 10
    }
});

const priceStatSwitchWrapperStyle = css({
    display: "flex",
    flexDirection: "column-reverse",
    justifyContent: "space-between",
    gap: 1,

    xs: {
        flexDirection: "row",
        alignItems: "flex-end"
    }
});

const headerRowStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 2,
    mb: 2
});

const cardsRowStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 2,
    mb: 2,

    md: {
        flexDirection: "row",
        alignItems: "stretch"
    }
});

const pairedCardsStyle = css({
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: 2,
    p: 1.5,
    borderRadius: 2,
    backgroundColor: "var(--colors-white)",
    border: "1px solid var(--colors-gray-400)",

    md: {
        gridTemplateColumns: "1fr 1fr",
        p: 3
    }
});

const negotiationBlockStyle = css({
    gridColumn: "1 / -1",
    display: "flex",
    flexDirection: "column",
    gap: 2,
    p: 2,
    borderRadius: 2,
    backgroundColor: "var(--colors-green-subtle)",

    md: {
        flexDirection: "row",
        alignItems: "center",
        p: 3
    }
});

const negotiationBadgeStyle = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 0.5,
    p: 1,
    borderRadius: 2,
    backgroundColor: "var(--colors-success)",
    minWidth: "10rem",
    flex: "0 0 auto",

    md: {
        py: 2,
        px: 2.5
    }
});
import {useSelector} from "react-redux";
import {css} from "@panda/css";

import {Container} from "@pg-design/grid-module";
import {Text} from "@pg-design/text-module";
import {SkeletonBox} from "@pg-mono/skeleton-box";
import {priceFormat, priceM2Format, roomsFormat} from "@pg-mono/string-utils";

import {IRPStore} from "../../../app/rp_reducer";
import {IRegionReportPrice} from "../../../region/types/IRegionReportPrice";
import {getTransactionPricesPeriod} from "../actions/fetch_transaction_prices";
import {useGetRcnDataQuery} from "../api/get_rcn_data_api";
import {PriceComparisonCard} from "../atoms/PriceComparisonCard";
import {isAllRooms} from "../constants/rcn_rooms_filter";
import {TRANSACTION_PRICES_GRAIN, TRANSACTION_PRICES_METRIC} from "../constants/transaction_prices";
import {PricesCityAnalyticsLocation} from "../tracking/prices_city_analytics";
import {formatMonthLabel} from "../utils/format_month_label";
import {formatRcnPeriodLabel} from "../utils/format_rcn_period_label";
import {formatTransactionsCountGenitive} from "../utils/format_transactions_count";
import {getAvgPriceM2ForRooms} from "../utils/get_avg_price_m2_for_rooms";
import {getFlatTypeGenitiveLabel} from "../utils/get_flat_type_genitive_label";
import {getLatestPriceReport} from "../utils/get_latest_price_report";
import {getMedianPriceM2} from "../utils/get_median_price_m2";
import {getPeriodOfferPriceM2} from "../utils/get_period_offer_price_m2";
import {getPricesAvailableDate} from "../utils/get_prices_available_date";
import {getRcnPriceStatsQuery} from "../utils/get_rcn_params";
import {getSignedPercentComma} from "../utils/percents_handling_helpers";
import {PricesCityViewSectionTracker} from "./PricesCityViewSectionTracker";
import {usePricesContext} from "./PricesContextFilterProvider";
import {PricesRegionSchemaDataset} from "./PricesRegionSchemaDataset";
import {PriceStatSwitch} from "./PriceStatSwitch";
import {RoomsFilterSelect} from "./RoomsFilterSelect";

interface Props {
    region: IRegionReportPrice;
    location: PricesCityAnalyticsLocation;
}

// Below this, the negotiation summary reads as "prices were close" rather than a forced %.
const NEGOTIATION_STABILITY_THRESHOLD_PERCENT = 1;

export const PricesDifferences = (props: Props) => {
    const {region, location} = props;
    const {rooms, priceStat} = usePricesContext();

    const prices = useSelector((store: IRPStore) => store.report.price.prices);
    const pricesAvailableDate = useSelector((store: IRPStore) => store.report.price.pricesAvailableDate);
    const referenceDate = getPricesAvailableDate(pricesAvailableDate?.date);

    // The hub table's query for this one region; only the rooms filter and the median switch tell them apart.
    const {data: rcnData, isFetching} = useGetRcnDataQuery(
        getRcnPriceStatsQuery(region.id, referenceDate, rooms, 1, TRANSACTION_PRICES_GRAIN, priceStat === "median" ? "median" : TRANSACTION_PRICES_METRIC)
    );
    // The quarter the hub table reports, so the city's figures match its row there.
    const reportedPeriod = getTransactionPricesPeriod(referenceDate);

    const currentMonthData = getLatestPriceReport(prices);

    if (!prices || !currentMonthData) {
        return null;
    }

    const currentOfferPriceM2 = priceStat === "median" ? getMedianPriceM2(currentMonthData) : getAvgPriceM2ForRooms(currentMonthData, rooms);

    const latestRcnPoint = rcnData?.series[0];
    const hasRcnData = latestRcnPoint !== undefined;
    // Only the reported quarter is shown - an older point just proves the registry has data for this region.
    const lastRcnPeriod = latestRcnPoint?.period_start === reportedPeriod.period_start ? latestRcnPoint : undefined;
    const transactionalPriceM2 = lastRcnPeriod?.value ?? null;

    // Falls back to the reported period when RCN has no data at all (e.g. Bydgoszcz).
    const offerPeriod = lastRcnPeriod ?? reportedPeriod;

    // Weighted average across the quarter's months (by offer count), not just one noisy month.
    const matchedOfferPriceM2 = getPeriodOfferPriceM2({
        history: prices,
        filter: (monthData) => monthData.slug_city === currentMonthData.slug_city,
        periodStart: offerPeriod.period_start,
        periodLabel: offerPeriod.period_label,
        getPriceM2: (monthData) => (priceStat === "median" ? getMedianPriceM2(monthData) : getAvgPriceM2ForRooms(monthData, rooms)),
        getWeight: (monthData) => monthData.available_offers
    });

    const isPricesLoading = isFetching;

    const differenceM2 = matchedOfferPriceM2 && transactionalPriceM2 ? matchedOfferPriceM2 - transactionalPriceM2 : null;
    const differencePercent = differenceM2 && matchedOfferPriceM2 ? Math.round((differenceM2 / matchedOfferPriceM2) * 1000) / 10 : null;

    return (
        <Container className={containerStyle}>
            <PricesCityViewSectionTracker sectionName="price_differences" location={location} />

            <div className={headerRowStyle}>
                <div>
                    <Text variant="headline_3" as="h2" mb={2}>
                        Ile kosztuje nowe mieszkanie {region.name_declension_where}
                    </Text>

                    <Text variant="body_copy_1" color="var(--colors-gray-700)" mb={2}>
                        Pokazujemy mediany i średnie ceny ofertowe z naszej bazy – czyli kwoty, za jakie deweloperzy oferują mieszkania – i zestawiamy je z
                        rzeczywistymi cenami transakcyjnymi z RCN. Dzięki temu widzisz, ile faktycznie płacą kupujący i jaka jest przestrzeń do negocjacji.
                    </Text>
                </div>
                <div className={priceStatSwitchWrapperStyle}>
                    <PriceStatSwitch location={location} />
                    <RoomsFilterSelect instanceId="prices-differences-rooms-select" sectionName="price_differences" location={location} />
                </div>
            </div>

            <div className={cardsRowStyle}>
                <PriceComparisonCard
                    variant="white"
                    title="Aktualna cena ofertowa"
                    priceM2={currentOfferPriceM2}
                    description="Aktualna kwota oczekiwana przez dewelopera – punkt wyjścia do negocjacji, nie ostateczna kwota transakcji."
                    source={`Źródło: baza ofert rynekpierwotny.pl · ${formatMonthLabel(currentMonthData.date)}`}
                    isLoading={isPricesLoading}
                    className={css({md: {minW: "320px"}})}
                />

                <div className={pairedCardsStyle}>
                    <PriceComparisonCard
                        variant="white"
                        title={`Cena ofertowa (${formatRcnPeriodLabel(offerPeriod.period_label)})`}
                        priceM2={matchedOfferPriceM2}
                        description={`${priceStat === "median" ? "Mediana kwoty" : "Średnia kwota"}, jakiej deweloper oczekiwał w ogłoszeniach w ${formatRcnPeriodLabel(offerPeriod.period_label)}.`}
                        source={`Źródło: baza ofert rynekpierwotny.pl · ${formatRcnPeriodLabel(offerPeriod.period_label)}`}
                        isLoading={isPricesLoading}
                    />

                    <PriceComparisonCard
                        variant="beige"
                        title={`Cena transakcyjna${
                            lastRcnPeriod
                                ? ` (${formatRcnPeriodLabel(lastRcnPeriod.period_label)}${lastRcnPeriod.n_rows < 10 ? ", dane orientacyjne" : ""})`
                                : ""
                        }`}
                        priceM2={transactionalPriceM2}
                        emptyStateMessage={hasRcnData ? "Za mało transakcji" : undefined}
                        description={
                            priceStat === "median"
                                ? "Mediana kwoty faktycznie zapłaconej, wpisanej do aktu notarialnego."
                                : "Średnia kwota faktycznie zapłacona, wpisana do aktu notarialnego."
                        }
                        source={`Źródło: RCN${lastRcnPeriod ? `, ${formatRcnPeriodLabel(lastRcnPeriod.period_label)}` : ""} · rynek pierwotny`}
                        sourceDetails={
                            lastRcnPeriod && (
                                <Text variant="info_txt_1" color="var(--colors-gray-700)" style={{lineHeight: "100%"}}>
                                    <span style={{fontWeight: 600}}>Na podstawie {formatTransactionsCountGenitive(lastRcnPeriod.n_rows)}</span>
                                    {!isAllRooms(rooms) ? ` · ${roomsFormat(rooms)}` : ""} · {region.short_name}
                                </Text>
                            )
                        }
                        isLoading={isPricesLoading}
                    />

                    {isPricesLoading ? (
                        <div className={negotiationBlockStyle}>
                            <div className={negotiationBadgeStyle}>
                                <SkeletonBox isText height="4.8rem" width="80%" />
                            </div>

                            <SkeletonBox isText height="4.8rem" width="100%" />
                        </div>
                    ) : (
                        // Non-null only when lastRcnPeriod, matchedOfferPriceM2 and transactionalPriceM2 all were.
                        differencePercent !== null &&
                        differenceM2 !== null && (
                            <div className={negotiationBlockStyle}>
                                <div className={negotiationBadgeStyle}>
                                    <Text variant="headline_3" as="p" color="var(--colors-secondary)" style={{textAlign: "center"}}>
                                        {getSignedPercentComma(differencePercent, true)}
                                    </Text>
                                    <Text variant="info_txt_1" color="var(--colors-gray-800)" style={{textAlign: "center"}}>
                                        {priceM2Format(Math.abs(differenceM2))}
                                    </Text>
                                </div>

                                <div>
                                    <Text variant="headline_6" as="p" color="var(--colors-gray-800)" mb={1}>
                                        Podsumowanie
                                    </Text>

                                    <Text variant="info_txt_1" color="var(--colors-gray-800)">
                                        W {formatRcnPeriodLabel(lastRcnPeriod!.period_label).replace("kw.", "kwartale")}{" "}
                                        {priceStat === "median" ? "mediana ceny ofertowej" : "średnia cena ofertowa"} {getFlatTypeGenitiveLabel(rooms)}{" "}
                                        {region.name_declension_where} wynosiła {priceM2Format(matchedOfferPriceM2!)}, a cena transakcyjna –{" "}
                                        {priceM2Format(transactionalPriceM2!)}. Oznacza to, że{" "}
                                        {Math.abs(differencePercent) <= NEGOTIATION_STABILITY_THRESHOLD_PERCENT
                                            ? "ceny transakcyjne były zbliżone do ofertowych – kupujący płacili mniej więcej tyle, ile wynikało z ogłoszeń."
                                            : `kupujący płacili${priceStat === "median" ? "" : " średnio"} o ${Math.abs(differencePercent)
                                                  .toString()
                                                  .replace(
                                                      ".",
                                                      ","
                                                  )}% ${differencePercent > 0 ? "mniej" : "więcej"} niż wynikało z ogłoszeń – około ${priceFormat(
                                                  Math.abs(differenceM2)
                                              )} za każdy metr kwadratowy.`}
                                    </Text>
                                </div>
                            </div>
                        )
                    )}
                </div>
            </div>

            <PricesRegionSchemaDataset
                region={region}
                offerPriceM2={matchedOfferPriceM2}
                transactionalPriceM2={transactionalPriceM2}
                lastRcnPeriod={lastRcnPeriod}
            />
        </Container>
    );
};

const containerStyle = css({
    my: 4.5,

    md: {
        my: 8
    },
    lg: {
        px: 10
    }
});

const priceStatSwitchWrapperStyle = css({
    display: "flex",
    flexDirection: "column-reverse",
    justifyContent: "space-between",
    gap: 1,

    xs: {
        flexDirection: "row",
        alignItems: "flex-end"
    }
});

const headerRowStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 2,
    mb: 2
});

const cardsRowStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 2,
    mb: 2,

    md: {
        flexDirection: "row",
        alignItems: "stretch"
    }
});

const pairedCardsStyle = css({
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: 2,
    p: 1.5,
    borderRadius: 2,
    backgroundColor: "var(--colors-white)",
    border: "1px solid var(--colors-gray-400)",

    md: {
        gridTemplateColumns: "1fr 1fr",
        p: 3
    }
});

const negotiationBlockStyle = css({
    gridColumn: "1 / -1",
    display: "flex",
    flexDirection: "column",
    gap: 2,
    p: 2,
    borderRadius: 2,
    backgroundColor: "var(--colors-green-subtle)",

    md: {
        flexDirection: "row",
        alignItems: "center",
        p: 3
    }
});

const negotiationBadgeStyle = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 0.5,
    p: 1,
    borderRadius: 2,
    backgroundColor: "var(--colors-success)",
    minWidth: "10rem",
    flex: "0 0 auto",

    md: {
        py: 2,
        px: 2.5
    }
});
